import React, { useState } from 'react';
import api from '../../utils/api';
import { isImageAttachment } from './KnowledgeMedia';

export function formatFileSize(value) {
  const size = Number(value) || 0;
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} МБ`;
}

function fileIcon(attachment) {
  const type = String(attachment?.content_type || '').toLowerCase();
  const name = String(attachment?.name || '').toLowerCase();
  if (type.startsWith('image/')) return '🖼';
  if (type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(name)) return 'VIDEO';
  if (type === 'application/pdf' || name.endsWith('.pdf')) return 'PDF';
  if (/\.(doc|docx)$/i.test(name)) return 'DOC';
  if (/\.(xls|xlsx)$/i.test(name)) return 'XLS';
  if (/\.(zip|7z)$/i.test(name)) return 'ZIP';
  return 'FILE';
}

export async function openKnowledgeAttachment(attachment) {
  const response = await api.get(attachment.embed_link_url || `/api/knowledge/files/${attachment.id}/embed-link`);
  const url = response.data?.url;
  if (!url) throw new Error('Файл недоступен');
  const absoluteUrl = new URL(url, window.location.origin).toString();
  if (window.Telegram?.WebApp?.openLink) {
    window.Telegram.WebApp.openLink(absoluteUrl);
    return;
  }
  const anchor = document.createElement('a');
  anchor.href = absoluteUrl;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.click();
}

export default function KnowledgeAttachments({
  attachments = [],
  onDelete = null,
  onInsert = null,
  embeddedIds = null,
  onSetCover = null,
  coverAttachmentId,
  deletingId = null,
  title = 'Файлы',
}) {
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  if (!attachments.length) return null;
  const embedded = embeddedIds == null ? null : new Set(embeddedIds.map(String));

  const openFile = async attachment => {
    setOpeningId(attachment.id);
    setError('');
    try {
      await openKnowledgeAttachment(attachment);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Не удалось открыть файл');
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <section className="knowledge-attachments" aria-label={title}>
      <div className="knowledge-attachments-heading">
        <h3>{title}</h3>
        <span>{attachments.length}</span>
      </div>
      {error && <div className="error-msg" role="alert">{error}</div>}
      <div className="knowledge-attachment-list">
        {attachments.map(attachment => {
          const isEmbedded = embedded ? embedded.has(String(attachment.id)) : attachment.is_embedded;
          const isCover = coverAttachmentId !== undefined
            ? String(coverAttachmentId || '') === String(attachment.id)
            : attachment.is_cover;
          const isImage = isImageAttachment(attachment);
          const coverEligible = isImage && attachment.cover_eligible !== false;
          return (
          <div key={attachment.id} className={`knowledge-attachment-row ${isEmbedded ? 'embedded' : ''} ${isCover ? 'cover' : ''}`}>
            <button type="button" className="knowledge-attachment-open" onClick={() => openFile(attachment)} disabled={openingId === attachment.id}>
              <span className="knowledge-attachment-icon" aria-hidden="true">{fileIcon(attachment)}</span>
              <span>
                <strong>{attachment.name}</strong>
                <small>{formatFileSize(attachment.size)}</small>
              </span>
              <span className="knowledge-attachment-action">{openingId === attachment.id ? 'Открываем…' : 'Открыть'}</span>
            </button>
            {(onInsert || onSetCover || onDelete) && (
              <div className="knowledge-attachment-tools">
                {isEmbedded && <span className="knowledge-attachment-badge">В статье</span>}
                {isCover && <span className="knowledge-attachment-badge cover">Обложка</span>}
                {onSetCover && isImage && !coverEligible && !isCover && (
                  <span className="knowledge-attachment-badge warning">Слишком большая для обложки</span>
                )}
                {onInsert && <button type="button" onClick={() => onInsert(attachment)}>Вставить</button>}
                {onSetCover && (coverEligible || isCover) && (
                  <button type="button" onClick={() => onSetCover(isCover ? null : attachment)}>
                    {isCover ? 'Снять обложку' : 'На обложку'}
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    className="knowledge-attachment-delete"
                    onClick={() => onDelete(attachment)}
                    disabled={deletingId === attachment.id}
                    aria-label={`Удалить ${attachment.name}`}
                  >
                    {deletingId === attachment.id ? '…' : 'Удалить'}
                  </button>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </section>
  );
}
